import { TypeReference } from '../../data/type-reference.js'
import type { NameAndType, TsTypeType } from '../../types.js'
import { LogicBlock } from './logic-block.js'
import type { TsCodeType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { Block } from '../block.js'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { BoVisitor } from '../../bos/bo-visitor.js'
import { Expression } from '../../data/text-or-code.js'
import { ExpressionOrCode } from '../../data/text-or-code.js'
import { Screen } from '../../bos/screen.js'

export const TrackByFunctions = [
	'trackByAutomatic',
	'trackById',
	'trackByIndex',
	'trackByItem',
] as const
export const TrackByFunctionNames: Record<
	(typeof TrackByFunctions)[number],
	string
> = {
	trackByAutomatic:
		'Automatic (Id if present, item if string/number, index otherwise)',
	trackById: 'Id',
	trackByIndex: 'Index',
	trackByItem: 'Looped item',
}

@jsonClass('ForBlock')
export class ForBlock extends LogicBlock {
	@jsonProperty(ExpressionOrCode)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Array',
		codeLanguage: 'ts',
		tsReturnType: () => 'any[]',
		editorSize: 'normal',
		isBinding: false,
		isMandatory: () => true,
		spanColumns: '1 / -1',
	})
	collectionCode = new ExpressionOrCode('[]')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'input',
		order: 2,
		label: 'Loop variable',
		contentType: 'text',
		elementType: 'input',
		isMandatory: () => true,
	})
	loopVariableDefinition: TsTypeType = 'item'

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 3,
		label: 'Loop variable type',
		codeLanguage: 'ts',
		tsReturnType: () => 'any',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	loopVariableType = new Expression('{}')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'input',
		order: 4,
		label: 'Index variable (0, 1, 2, ...)',
		contentType: 'text',
		elementType: 'input',
		tooltip: () => ({
			text: 'If empty, the index variable will be the loop variable plus "Index". The first item always has index zero',
		}),
	})
	indexVariableDefinition: TsTypeType = ''

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 5,
		label: 'Track by',
		options: TrackByFunctions.map((f) => [f, TrackByFunctionNames[f]]),
	})
	trackBy: (typeof TrackByFunctions)[number] = 'trackByAutomatic'

	canAcceptVariableString = (variable: NameAndType) => !!variable.isArray
	acceptVariableString = (variable: NameAndType, screen: Screen) => {
		this.collectionCode = new ExpressionOrCode(variable.name, 'E')
		this.loopVariableType = new Expression(
			TypeReference.adaptTypeForThisModule(variable.type, screen.moduleId),
		)
	}

	constructor(init: Partial<ForBlock> = {}) {
		super()
		this.init(init)
	}

	getEditorTitle() {
		return `${super.getEditorTitle()} ${this.loopVariableDefinition} in ${
			this.collectionCode.content
		}`
	}

	getTitleBackgroundAndBorderColor(): string {
		return '#64b5f6'
	}

	getTitleColor(): string {
		return '#0d2744'
	}

	getIndexVariableName() {
		return (
			this.indexVariableDefinition?.trim() ||
			this.loopVariableDefinition + 'Index'
		)
	}

	public getOwnVariables(
		includeDeclarationVariables: boolean,
	): Record<string, string | null> {
		return {
			[this.loopVariableDefinition]: this.loopVariableType.content,
			[this.getIndexVariableName()]: 'number',
		}
	}

	protected visitThisWithBoVisitor(
		visitor: BoVisitor,
		pathPrefix: (string | number)[],
	): void {
		this.visitCodePropertyWithBoVisitor(
			visitor,
			pathPrefix,
			ForBlock,
			'collectionCode',
		)
		this.visitCodePropertyWithBoVisitor(
			visitor,
			pathPrefix,
			ForBlock,
			'loopVariableType',
		)
	}

	getEditorCategory(): string {
		return 'Logic'
	}
}
